import React from "react";
// Relative, unlike its shadcn neighbours' "@/lib/utils": this component is rendered by
// every validated form, so it is pulled into ~70 component tests, and the "@" alias does
// not exist under Jest. Resolving for real beats making each of those tests declare a
// virtual passthrough mock (the same trade lib/api.js made with "./apiErrors").
import { cn } from "../../lib/utils";

/**
 * The single rendering of a field-level validation message.
 *
 * `role="alert"` is the point: the message appears AFTER the user acted (a refused
 * submit), so a screen reader that only announces on focus would never say it. It also
 * means the markup is identical everywhere — a per-form `<p className="text-xs
 * text-red-600">` drifts in colour, spacing and test id, and none of it is greppable.
 *
 * Renders NOTHING when there is no message: callers pass `error && ...` state directly
 * without a guard, and an empty `<p role="alert">` would still be announced as an alert.
 *
 * @param {string} [message] resolved, user-visible text (the caller applies `t` to the
 *   i18n key held in state — see lib/validation.js).
 * @param {string} [testId] `data-testid`, by convention `${fieldTestId}-error`.
 * @param {string} [className] extra classes, merged (tailwind-merge wins on conflicts).
 */
export default function FieldError({ message, testId, className }) {
  if (!message) return null;
  return (
    <p role="alert" className={cn("text-xs text-red-600 mt-1", className)} data-testid={testId}>
      {message}
    </p>
  );
}

/**
 * Border treatment for the input the message belongs to. Colour alone is not the signal —
 * the message under the field is — but the border is what makes the offending field
 * findable in a long form without reading every label.
 */
export const invalidInputClass = "border-red-500 focus:border-red-500";
